/**
 * GoalProgress — Phase 2B.
 *
 * Shows the top non-FI goal. FI goals are EXCLUDED entirely per
 * Amendment 4 / 0.2b.5.
 *
 * `on_track` comes from snapshot.goals.items[].on_track (server-
 * computed). We NEVER compute it client-side (Amendment 6).
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, Target } from 'lucide-react';
import { formatMoney } from '@/lib/format';

function Ring({ pct }) {
  const R = 32, C = 2 * Math.PI * R;
  const clamped = Math.max(0, Math.min(100, pct || 0));
  const offset = C * (1 - clamped / 100);
  return (
    <svg width="80" height="80" viewBox="0 0 80 80" aria-hidden>
      <circle cx="40" cy="40" r={R} stroke="hsl(var(--muted))" strokeWidth="6" fill="none" />
      <circle
        cx="40" cy="40" r={R}
        stroke="hsl(var(--primary))" strokeWidth="6" fill="none"
        strokeLinecap="round"
        strokeDasharray={C} strokeDashoffset={offset}
        transform="rotate(-90 40 40)"
      />
      <text x="40" y="44" textAnchor="middle" fontSize="14" fontWeight="600" fill="hsl(var(--foreground))">
        {Math.round(clamped)}%
      </text>
    </svg>
  );
}

export default function GoalProgress({
  topGoal = null,           // snapshot.goals.items[i]  (already FI-filtered by parent)
  onTrackCount = 0,
  offTrackCount = 0,
  loading = false,
  baseCurrency = 'INR',
  onOpenDrawer,
}) {
  if (loading) {
    return (
      <Card data-testid="dashboard-goal-progress">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Goal progress</CardTitle></CardHeader>
        <CardContent className="flex gap-4"><Skeleton className="h-20 w-20 rounded-full" /><div className="flex-1 space-y-2"><Skeleton className="h-4 w-3/4" /><Skeleton className="h-4 w-2/4" /></div></CardContent>
      </Card>
    );
  }

  if (!topGoal) {
    return (
      <Card data-testid="dashboard-goal-progress">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Goal progress</CardTitle></CardHeader>
        <CardContent>
          <div className="py-2">
            <p className="text-sm text-muted-foreground">No active goal yet. Setting one makes progress visible.</p>
            <Button asChild variant="ghost" className="px-0 mt-2 text-primary hover:bg-transparent">
              <Link to="/plan-ahead" data-testid="goal-empty-cta">Set a goal <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const onTrackPill = topGoal.on_track === true
    ? { label: 'On track', className: 'bg-emerald/15 text-emerald' }
    : topGoal.on_track === false
      ? { label: 'Off track', className: 'bg-amber/15 text-amber' }
      : null;

  return (
    <Card data-testid="dashboard-goal-progress">
      <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Goal progress</CardTitle></CardHeader>
      <CardContent>
        <div className="flex items-start gap-4">
          <Ring pct={topGoal.progress_pct} />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-foreground truncate" data-testid="goal-title">{topGoal.title}</div>
            <div className="text-xs text-muted-foreground num mt-0.5">
              {formatMoney(topGoal.current_amount_base || 0, baseCurrency)} of {formatMoney(topGoal.target_amount_base || 0, baseCurrency)}
            </div>
            {onTrackPill && (
              <span className={`inline-block mt-2 text-[11px] font-medium px-2 py-0.5 rounded-full ${onTrackPill.className}`} data-testid="goal-on-track-pill">
                {onTrackPill.label}
              </span>
            )}
            <div className="text-[11px] text-muted-foreground mt-2">
              <span data-testid="goal-on-track-count">{onTrackCount}</span> on track · <span data-testid="goal-off-track-count">{offTrackCount}</span> off track
            </div>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <Button variant="ghost" className="px-0 text-primary hover:bg-transparent" onClick={() => onOpenDrawer?.('goal', topGoal.id)} data-testid="goal-open-drawer">
            <Target className="mr-1.5 h-3.5 w-3.5" /> Details
          </Button>
          <Button asChild variant="ghost" className="px-0 text-primary hover:bg-transparent">
            <Link to="/plan-ahead" data-testid="goal-all-link">All goals <ArrowRight className="ml-1.5 h-3.5 w-3.5" /></Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
